import { useTranslation } from 'react-i18next'
import { Divider } from '../../../../components/generic.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { useLightningReportTypeRules } from '../../../../data/analyze/reportTypeRules.ts'
import type { AssetLocation } from '../../../../data/locations.tsx'
import { LightningReportFormPageSummaryHeader } from '../../LightningReportFormPageSummaryHeader.tsx'
import { defineReportType, type ReportFormProps } from '../CreateReportForm.tsx'
import { CustomPointField } from '../fields/CustomPointField.tsx'
import { FieldErrorAlert } from '../fields/FieldErrorAlert.tsx'
import { LocationList } from '../fields/LocationList.tsx'
import { NameField } from '../fields/NameField.tsx'
import { RadiusField } from '../fields/RadiusField.tsx'
import { TimezoneField } from '../fields/TimezoneField.tsx'
import { UpdateGeometryWhenDependentFieldsChange } from '../fields/UpdateGeometryWhenDependentFieldsChange.tsx'
import { isNonEmpty } from '../schema.ts'
import { useDefaultReportName } from '../useDefaultReportName.ts'
import { DatasetField } from './fields/DatasetField.tsx'
import {
	ErrorEllipseConfidenceField,
	IncludeCloudToCloudField,
	IncludeStrikeDamagePotentialField,
	SignalStrengthFilterField,
} from './fields/LightningReportDetailsFields.tsx'
import { LightningTimeSelection } from './fields/LightningTimeSelection.tsx'
import { NldnBoundaryLayer } from './map/NldnBoundaryLayer.tsx'
import {
	type LightningReportForm,
	oneOffTimingToTimeRange,
	useLightningDefaultValues,
	useLightningFormSchema,
	useLightningFormValues,
} from './schema.tsx'
import { useLightningSubmit } from './useLightningSubmit.tsx'

export const useLightningReportType = (props: ReportFormProps) => {
	const rules = useLightningReportTypeRules()
	const schema = useLightningFormSchema()
	const defaultValues = useLightningDefaultValues(props.selectedLocations)
	const onSubmit = useLightningSubmit()
	const defaultName = useLightningReportName(props.selectedLocations)
	const summaryHeader = ({ showTiming }: { showTiming: boolean }) => (
		<LightningSummaryHeader selectedLocations={props.selectedLocations} showTiming={showTiming} />
	)
	const reportLocations = rules.maxAssets > 1 ? props.selectedLocations : props.selectedLocations.slice(0, 1)

	return defineReportType<LightningReportForm>({
		title: ($) => $.reports.lightning.form.newLightningAnalysisReport,
		schema,
		defaultValues,
		onSubmit,
		mapLayers: <NldnBoundaryLayer />,
		maxAssets: rules.maxAssets,
		steps: [
			{
				id: 'areaOfInterest',
				label: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.areaOfInterest.longDescription} />,
				mapInteractive: true,
				form: (
					<>
						<LocationList {...props} allowUnprotectedAssets={rules.allowUnprotectedAssets} maxAssets={rules.maxAssets} />
						<RadiusField selectedLocations={reportLocations} maxRadiusKm={150} maxAssets={rules.maxAssets} />
						<FieldErrorAlert name='geometry' />
						<CustomPointField
							selectedLocation={reportLocations.length === 1 ? reportLocations[0] : undefined}
							maxAssets={rules.maxAssets}
						/>
						<DatasetField />
						<UpdateGeometryWhenDependentFieldsChange maxVertexCount={rules.maxVertexCount} />
					</>
				),
				fieldsToValidate: ['assetVrns', 'customPoint', 'geometry', 'geometryTooComplex', 'geometryPending', 'radiusKm', 'dataset'],
			},
			{
				id: 'timeRange',
				label: <I18n i18nKey={($) => $.reports.form.steps.timeRange.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.timeRange.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.timeRange.longDescription} />,
				header: summaryHeader({ showTiming: false }),
				form: (
					<>
						<TimezoneField />
						<LightningTimeSelection />
					</>
				),
				fieldsToValidate: ['preferences.timezone', 'timing'],
			},
			{
				id: 'reportDetails',
				label: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.label} />,
				shortDescription: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.shortDescription} />,
				longDescription: <I18n i18nKey={($) => $.reports.form.steps.reportDetails.longDescription} />,
				header: summaryHeader({ showTiming: true }),
				form: (
					<>
						<NameField defaultName={defaultName} />
						<Divider />
						<ErrorEllipseConfidenceField />
						<IncludeStrikeDamagePotentialField />
						<Divider />
						<IncludeCloudToCloudField />
						<SignalStrengthFilterField />
					</>
				),
				fieldsToValidate: ['name', 'errorEllipseConfidence', 'signalStrengthRange', 'includeStrikeDamagePotential', 'includeCloudToCloud'],
			},
		],
	})
}

const useLightningReportName = (selectedLocations: AssetLocation[]) => {
	const { t } = useTranslation()
	const defaultReportName = useDefaultReportName()

	return (values: LightningReportForm) => {
		const suffixes = values.dataset === 'NLDN' ? ['reprocessed'] : []
		const report = {
			locationNames: selectedLocations.map(({ name }) => name),
			radiusKm: values.radiusKm,
			timezone: values.preferences.timezone,
		}

		switch (values.timing.type) {
			case 'ONE_OFF':
				return defaultReportName({ ...report, timeRange: oneOffTimingToTimeRange(values.timing), suffixes })
			case 'REPEATED': {
				const { frequency } = values.timing
				return defaultReportName({ ...report, suffixes: [t(($) => $.reports.lightning.common.frequencies[frequency]), ...suffixes] })
			}
			default:
				values.timing satisfies never
				throw new Error('Unrecognized timing')
		}
	}
}

const LightningSummaryHeader = ({ selectedLocations, showTiming }: { selectedLocations: AssetLocation[]; showTiming: boolean }) => {
	const [radiusKm, customPoint, timing, timezone, dataset] = useLightningFormValues([
		'radiusKm',
		'customPoint',
		'timing',
		'preferences.timezone',
		'dataset',
	])
	const assetVrns = selectedLocations.map((location) => location.assetVrn)
	if (!isNonEmpty(assetVrns)) return null

	return (
		<LightningReportFormPageSummaryHeader
			assetVrns={assetVrns}
			dataset={dataset}
			radiusKm={radiusKm}
			customPoint={customPoint}
			timezone={timezone}
			timeRange={showTiming && timing.type === 'ONE_OFF' ? oneOffTimingToTimeRange(timing) : undefined}
			frequency={showTiming && timing.type === 'REPEATED' ? timing.frequency : undefined}
			scheduleRange={
				showTiming && timing.type === 'REPEATED'
					? { startScheduleAt: timing.startScheduleAt, endScheduleAt: timing.stopScheduleAt }
					: undefined
			}
		/>
	)
}
